import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt,
    faCube,
    faEnvelope,
    faStore,
    faShareAlt,
    faBoxOpen,
    faGift,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { profilePictureUrl } from '../../utils/storageUrls';
import TrophyDisplay from './TrophyDisplay';

const ProfileHeader = ({ profileData, trophies, slug, isWishlist, isOwner, copiedUrl, onShare }) => {
    if (!profileData) return null;

    const displayName = profileData.display_name || profileData.username;

    const handleImageError = (e) => {
        e.target.src = '/images/lego_user.png';
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-8 overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 h-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-black bg-opacity-10" aria-hidden="true" />
            </div>

            <div className="px-6 sm:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-white shadow-xl overflow-hidden bg-white ring-4 ring-gray-100">
                            <img
                                src={profilePictureUrl(profileData.profile_picture) || '/images/lego_user.png'}
                                alt={displayName}
                                className="w-full h-full object-cover"
                                onError={handleImageError}
                            />
                        </div>
                    </div>

                    {/* User info */}
                    <div className="flex-grow text-center md:text-left">
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">{displayName}</h1>

                        <div className="flex flex-wrap gap-3 justify-center md:justify-start mb-4">
                            {profileData.location && (
                                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-full text-gray-700 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-500 mr-2" />
                                    <span>{profileData.location}</span>
                                </div>
                            )}
                            {profileData.favorite_theme_name && (
                                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-full text-blue-700 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200">
                                    <FontAwesomeIcon icon={faCube} className="text-blue-600 mr-2" />
                                    <span>{profileData.favorite_theme_name}</span>
                                </div>
                            )}
                        </div>

                        {trophies && trophies.length > 0 && (
                            <div className="mb-4">
                                <TrophyDisplay trophies={trophies} />
                            </div>
                        )}

                        {profileData.bio && (
                            <p className="text-gray-600 whitespace-pre-line max-w-3xl leading-relaxed text-base">{profileData.bio}</p>
                        )}
                    </div>

                    {/* Actions + social */}
                    <div className="flex-shrink-0 flex flex-col items-center md:items-end gap-4">
                        {(profileData.twitter_handle || profileData.youtube_channel || profileData.bricklink_store || profileData.email) && (
                            <div className="flex gap-3">
                                {profileData.twitter_handle && (
                                    <a
                                        href={`https://twitter.com/${profileData.twitter_handle}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                                    >
                                        <FontAwesomeIcon icon={faTwitter} />
                                    </a>
                                )}
                                {profileData.youtube_channel && (
                                    <a
                                        href={`https://youtube.com/${profileData.youtube_channel}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                                    >
                                        <FontAwesomeIcon icon={faYoutube} />
                                    </a>
                                )}
                                {profileData.bricklink_store && (
                                    <a
                                        href={`https://store.bricklink.com/${profileData.bricklink_store}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                                    >
                                        <FontAwesomeIcon icon={faStore} />
                                    </a>
                                )}
                                {profileData.email && (
                                    <a
                                        href={`mailto:${profileData.email}`}
                                        className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                                    >
                                        <FontAwesomeIcon icon={faEnvelope} />
                                    </a>
                                )}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                            {isWishlist ? (
                                <Link
                                    to={`/collection/${encodeURIComponent(profileData?.username || slug)}`}
                                    className="px-5 py-2.5 bg-gradient-to-r from-white to-gray-50 border border-gray-300 hover:from-gray-50 hover:to-gray-100 text-gray-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium"
                                >
                                    <FontAwesomeIcon icon={faBoxOpen} className="mr-2" />
                                    View Collection
                                </Link>
                            ) : (
                                profileData?.has_wishlist && (
                                    <Link
                                        to={`/wishlist/${encodeURIComponent(profileData?.username || slug)}`}
                                        className="px-5 py-2.5 bg-gradient-to-r from-white to-gray-50 border border-gray-300 hover:from-gray-50 hover:to-gray-100 text-gray-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium"
                                    >
                                        <FontAwesomeIcon icon={faGift} className="mr-2" />
                                        View Wishlist
                                    </Link>
                                )
                            )}

                            <button
                                onClick={onShare}
                                className="px-5 py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-800 rounded-xl transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                            >
                                <FontAwesomeIcon icon={copiedUrl ? faCheck : faShareAlt} className="mr-2" />
                                {copiedUrl ? 'Copied!' : 'Share'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileHeader;
